import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import React, { PropsWithChildren, useEffect, useRef } from "react";
import MainHeader from "./presentation/screens/header/MainHeader";
import { Outlet, useNavigate } from "react-router";
import { NotFoundPage } from "./presentation/screens/not-found/NotFoundPage";
import MainFooter from "./presentation/screens/footer/MainFooter";
import { useStyleRepository } from "./data/repositories/StyleRepository";
import { useDesignRepository } from "./data/repositories/DesignRepository";
import { useLogoRepository } from "./data/repositories/LogoRepository";
import { useProfileRepository } from "./data/repositories/ProfileRepository";
import { useOrderRepository } from "./data/repositories/OrderRepository";
import { useManufacturerRepository } from "./data/repositories/ManufacturerRepository";
import { useUserRepository } from "./data/repositories/UserRepository";
import { useWhiteLabelContext, WhiteLabelProvider } from "./WhiteLabelContext";
import { ProfileProvider } from "./contexts/ProfileContext";
import {
  RepositoriesProvider,
  useRepositoriesContext,
} from "./contexts/RepositoriesContext";
import LoadingElement from "./presentation/components/loading-element/loading-element";
import { useOrganizationRepository } from "./data/repositories/OrganizationRepository";
import { CartProvider } from "./contexts/CartContext";
import { useCountryRepository } from "./data/repositories/CountryRepository";
import { useCbsConfigRepository } from "./data/repositories/CbsConfigRepository";
import { useStatisticsRepository } from "./data/repositories/StatisticsRepository";
import { useApiKeyRepository } from "./data/repositories/ApiKeyRepository";
import { usePlaceOrderQueueRepository } from "./data/repositories/PlaceOrderQueueRepository";
import { useTransferMethodConfigRepository } from "./data/repositories/TransferMethodConfigRepository";
import { useDesignFolderRepository } from "./data/repositories/DesignFolderRepository";
import { useContentRepository } from "./data/repositories/ContentRepository";
import { CountryProvider } from "./presentation/screens/design-editor2/contexts/country.context";
import { PageQuery } from "./utils/models/pageQuery";
import { InteractionStatus } from "@azure/msal-browser";
import { JUST_LOGGED_OUT_KEY, loginRequest } from "./AuthConfig";
import { OrganizationImpersionationProvider } from "./contexts/OrganizationImpersonationContext";
import { LogoProvider } from "./presentation/screens/design-editor2/contexts/logo-options.context";
import { PriceVisibilityProvider } from "./contexts/PriceVisibilityContext";
import { getInitialQuery } from "./presentation/shared/functions/getInitialQuery.function";
import { getAllQuery } from "./presentation/screens/logo-editor/logo-editor.screen";

interface Props {
  notfound?: boolean;
}

interface ScreenProps extends React.HTMLAttributes<HTMLDivElement> {
  style?: React.CSSProperties & {
    [key: `--${string}`]: string | number;
  };
}

const Screen = React.forwardRef<HTMLDivElement, ScreenProps>(
  ({ children, className, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        {...rest}
        className={
          "app w-screen min-h-screen h-screen overflow-auto " +
          (className ?? "")
        }
      >
        {children}
      </div>
    );
  },
);

export function App({ notfound = false }: Props): React.ReactElement {
  function MainView() {
    function AuthenticatedView() {
      const { whiteLabel } = useWhiteLabelContext();
      return (
        <Screen
          style={{
            "--primary": whiteLabel.colorHex,
            "--primary-bg": `${whiteLabel.colorHex}10`,
          }}
        >
          <div className={"w-full fixed z-20 h-16 top-0"}>
            <MainHeader />
          </div>
          <div className={"relative"}>
            <div
              style={{
                minHeight: "100vh",
                paddingTop: "6.75rem",
                paddingBottom: "5rem",
                marginBottom: "-4.05rem",
              }}
              className={"w-full table  h-full bg-background"}
            >
              <div className="max-w-[90rem] w-full max-h-full h-full mx-auto">
                {notfound ? <NotFoundPage /> : <Outlet />}
              </div>
            </div>
            <div className={"w-full h-16 absolute z-20 bottom-[1px]"}>
              <MainFooter />
            </div>
          </div>
        </Screen>
      );
    }

    return (
      <>
        <AuthenticatedTemplate>
          <PreLoader>
            <AuthenticatedView />
          </PreLoader>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <UnauthenticatedView />
        </UnauthenticatedTemplate>
      </>
    );
  }

  return <MainView />;
}

export function PublicView() {
  return (
    <Screen style={{ "--primary": "#7BBB58", "--primary-bg": `#7BBB5810` }}>
      <div className={"relative"}>
        <div
          style={{
            minHeight: "100vh",
            paddingTop: "4rem",
            paddingBottom: "5rem",
            marginBottom: "-4.05rem",
          }}
          className={"w-full table h-full bg-background"}
        >
          <div className="max-w-[90rem] w-full max-h-full h-full mx-auto">
            {false ? <NotFoundPage /> : <Outlet />}
          </div>
        </div>
      </div>
    </Screen>
  );
}

function AuthenticatedView() {
  const { whiteLabel } = useWhiteLabelContext();

  useEffect(() => {
    const hostname = window.location.hostname.toLowerCase();
    const baseUrl =
      "https://stkitmakerdev.blob.core.windows.net/b2c-login-page/";
    let title = "KitMaker";
    let favicon = "/favicon.ico";

    if (hostname.includes("uk.jetsport.dk")) {
      title = "ID Unlimited - Webshop";
      favicon = baseUrl + "favicon-id-unlimited.ico";
    } else if (hostname.includes("es.jetsport.dk")) {
      title = "ID Unlimited - Webshop";
      favicon = baseUrl + "favicon-id-unlimited.ico";
    } else if (hostname.includes("js.jetsport.dk")) {
      title = "Jet Sport - Webshop";
      favicon = baseUrl + "favicon-jetsport.ico";
    } else if (hostname.includes("ns.jetsport.dk")) {
      title = "NS System - Webshop";
      favicon = baseUrl + "favicon-nssystem.ico";
    } else if (hostname.includes("dos4u.jetsport.dk")) {
      title = "DOS4U - Webshop";
      favicon = baseUrl + "favicon-dos4u.ico";
    }

    document.title = title;

    let link = document.querySelector(
      "link[rel*='icon']",
    ) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "shortcut icon";
      document.head.appendChild(link);
    }
    link.type = "image/x-icon";
    link.href = favicon;
  }, []);

  return (
    <Screen
      style={{
        "--primary": whiteLabel.colorHex,
        "--primary-bg": `${whiteLabel.colorHex}10`,
      }}
    >
      <div className={"w-full fixed z-20 h-16 top-0"}>
        <MainHeader />
      </div>
      <div className={"relative"}>
        <div
          style={{
            minHeight: "100vh",
            paddingTop: "6.75rem",
            paddingBottom: "5rem",
            marginBottom: "-4.05rem",
          }}
          className={"w-full table  h-full bg-background"}
        >
          <div className="max-w-[90rem] w-full max-h-full h-full mx-auto">
            {false ? <NotFoundPage /> : <Outlet />}
          </div>
        </div>
        <div className={"w-full h-16 absolute z-20 bottom-[1px]"}>
          <MainFooter />
        </div>
      </div>
    </Screen>
  );
}

// During Startup/HandleRedirect neither template renders, which used to leave
// a blank white screen on every auth round-trip (login, logout-return, token
// redirect) - the "blinking". Show the same splash instead so the whole
// journey is one steady loading state.
function BootSplash() {
  const { inProgress } = useMsal();
  if (
    inProgress !== InteractionStatus.Startup &&
    inProgress !== InteractionStatus.HandleRedirect
  ) {
    return null;
  }
  return (
    <Screen>
      <LoadingElement />
    </Screen>
  );
}

export function PrivateView() {
  return (
    <>
      <BootSplash />
      <AuthenticatedTemplate>
        <PreLoader>
          <AuthenticatedView />
        </PreLoader>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <UnauthenticatedView />
      </UnauthenticatedTemplate>
    </>
  );
}

function UnauthenticatedView() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // A deliberate logout (marker set in useNavigationBarViewModel.logout) must
  // NOT auto-start a new login - Safari's surviving B2C SSO cookie made that
  // bounce users straight back in. The marker lives in localStorage because
  // Safari drops sessionStorage across the cross-site logout round-trip.
  // postLogoutRedirectUri is '/' so the post-logout landing is always '/'; on
  // any other path (deep link later) we drop the marker and keep the normal
  // login-required flow.
  const justLoggedOut = localStorage.getItem(JUST_LOGGED_OUT_KEY) !== null;
  if (justLoggedOut && window.location.pathname !== "/") {
    localStorage.removeItem(JUST_LOGGED_OUT_KEY);
  } else if (justLoggedOut) {
    return (
      <Screen>
        <div className="flex h-screen flex-col items-center justify-center gap-5">
          <p className="text-lg font-semibold text-gray-800">
            You have been signed out.
          </p>
          <button
            type="button"
            disabled={inProgress !== InteractionStatus.None}
            onClick={() => {
              // Clear the marker BEFORE redirecting: if the login round-trip
              // fails, the next render falls through to the normal auto-login
              // instead of stranding the user on this screen.
              localStorage.removeItem(JUST_LOGGED_OUT_KEY);
              if (inProgress === InteractionStatus.None) {
                // prompt:'login' forces B2C to ask for credentials - after a
                // deliberate logout a surviving SSO cookie must not silently
                // sign the user back in (Safari kept its SSO session alive).
                instance
                  .loginRedirect({ ...loginRequest, prompt: "login" })
                  .catch(() => {});
              }
            }}
            className="rounded-md bg-gray-900 px-8 py-2.5 font-semibold text-white transition hover:opacity-85"
          >
            Sign in
          </button>
        </div>
      </Screen>
    );
  }

  if (!isAuthenticated && inProgress === InteractionStatus.None) {
    sessionStorage.setItem(
      "redirectOnLogin",
      window.location.pathname + window.location.search,
    );
    instance.loginRedirect(loginRequest);
  }

  return (
    <Screen>
      <LoadingElement />
    </Screen>
  );
}

export function PreLoader(props: PropsWithChildren) {
  const styleRepository = useStyleRepository();
  const designRepository = useDesignRepository();
  const logoRepository = useLogoRepository();
  const organizationRepository = useOrganizationRepository();
  const orderRepository = useOrderRepository();
  const manufacturerRepository = useManufacturerRepository();
  const userRepository = useUserRepository();
  const countryRepository = useCountryRepository();
  const statisticsRepository = useStatisticsRepository();
  const cbsConfigRepository = useCbsConfigRepository();
  const apiKeyRepository = useApiKeyRepository();
  const placeOrderQueueRepository = usePlaceOrderQueueRepository();
  const transferMethodConfigRepository = useTransferMethodConfigRepository();
  const designFolderRepository = useDesignFolderRepository();
  const contentRepository = useContentRepository();

  const repositories = {
    styleRepository,
    designRepository,
    logoRepository,
    organizationRepository,
    orderRepository,
    manufacturerRepository,
    userRepository,
    countryRepository,
    statisticsRepository,
    cbsConfigRepository,
    apiKeyRepository,
    placeOrderQueueRepository,
    transferMethodConfigRepository,
    designFolderRepository,
    contentRepository,
  };

  const { useProfile } = useProfileRepository();
  const { isLoading, refetch } = useProfile();
  const { accounts } = useMsal();
  const id = accounts[0]?.homeAccountId;

  // Refetch only when the account actually changes. The old unconditional
  // mount refetch restarted the just-started profile call (React Query's
  // cancelRefetch defaults to true), doubling GetProfile on every login -
  // and login always arrives via a full B2C redirect (fresh cache), so the
  // first mount has nothing stale to refetch.
  const prevId = useRef(id);
  useEffect(() => {
    // The prev-value guard (not the deps array) decides whether to refetch,
    // so extra effect runs from changing function identities are harmless.
    if (prevId.current === id) return;
    prevId.current = id;
    refetch();
  }, [id, refetch]);

  // Backstop: a stale "just logged out" marker must never survive into an
  // authenticated session (e.g. a logout that set it but never completed).
  useEffect(() => {
    localStorage.removeItem(JUST_LOGGED_OUT_KEY);
  }, []);

  const redirectOnLogin = sessionStorage.getItem("redirectOnLogin");
  const navigate = useNavigate();

  useEffect(() => {
    if (redirectOnLogin) {
      sessionStorage.removeItem("redirectOnLogin");
      navigate(redirectOnLogin);
    }
  }, [redirectOnLogin]);

  // Warm the destination screen's blocking queries in parallel with the
  // profile call happening above (behind the splash). Today those queries only
  // start once the profile has finished - a purely architectural
  // serialization. This is fail-silent cache warming with the exact keys the
  // screens use; on a miss the screens fetch exactly as before, so behaviour
  // is unchanged. The redirectOnLogin value captured on first render is used
  // because the effect above clears sessionStorage; window.location covers
  // authenticated reloads where no redirect marker exists.
  useEffect(() => {
    // Exact-match the destination: prefetching the default first page for a
    // deep link (e.g. /logo?pageIndex=3) would fire a heavy list request
    // whose cache key the screen would never read.
    // window.location covers authenticated reloads - but only without query
    // params (a reload on /logo?pageIndex=3 must not warm the default page-1
    // keys the screen never reads).
    const destination =
      redirectOnLogin ||
      (window.location.search === "" ? window.location.pathname : "");
    if (destination === "/" || destination === "/logo") {
      // The logo list gates on logos + options + customers + vendors;
      // countries feeds PreloadData. getInitialQuery(new URLSearchParams())
      // and getAllQuery are exactly what the screens compute, so the cache
      // keys match by construction (same precedent as the cross-prefetch).
      logoRepository.prefetchLogos(getInitialQuery(new URLSearchParams()));
      logoRepository.prefetchOptions();
      organizationRepository.prefetchAllCustomers(getAllQuery);
      organizationRepository.prefetchAllVendors(getAllQuery);
      countryRepository.prefetchAllCountries({
        page: { index: 0, size: 0 },
      } as PageQuery);
    } else if (destination === "/design") {
      // Mirror wave for the design list's heaviest gates.
      designRepository.prefetchDesigns(getInitialQuery(new URLSearchParams()));
      styleRepository.prefetchStyles(getAllQuery);
      manufacturerRepository.prefetchManufacturers();
    } else if (destination === "/order") {
      // Mirror wave for the order list's gates: orders + customers + vendors
      // (useCustomerAndVendors). getInitialQuery(new URLSearchParams(), 25)
      // is exactly what the screen's usePageQuery(25) computes on a fresh
      // visit, so the ["orders", "?pageSize=25&pageIndex=1"] cache key
      // matches by construction. List pages only - GetOrder details are
      // never prefetched.
      orderRepository.prefetchOrders(getInitialQuery(new URLSearchParams(), 25));
      organizationRepository.prefetchAllCustomers(getAllQuery);
      organizationRepository.prefetchAllVendors(getAllQuery);
    }
    // Mount-only by design (repository objects are recreated every render).
    // eslint-disable-next-line
  }, []);

  return !accounts[0] || isLoading ? (
    // Splash instead of a blank screen while the profile loads after login -
    // keeps the boot journey one continuous loading state.
    <Screen>
      <LoadingElement />
    </Screen>
  ) : (
    <RepositoriesProvider repositories={repositories}>
      <OrganizationImpersionationProvider>
        <ProfileProvider>
          <WhiteLabelProvider>
            <CartProvider>
              <PriceVisibilityProvider>
                <PreloadData>{props.children}</PreloadData>
              </PriceVisibilityProvider>
            </CartProvider>
          </WhiteLabelProvider>
        </ProfileProvider>
      </OrganizationImpersionationProvider>
    </RepositoriesProvider>
  );
}

function PreloadData(props: PropsWithChildren) {
  const { logoRepository, countryRepository } = useRepositoriesContext();
  const { useAllCountries } = countryRepository;
  const { data } = logoRepository.useOptions();
  const { data: countries } = useAllCountries({
    page: { index: 0, size: 0 },
  } as PageQuery);

  // Non-blocking preload: render the app immediately and let options/countries load in
  // the background (they only feed the design editor's pickers and country dropdowns).
  // Previously this returned <></> until both resolved, holding every page's first paint
  // and delaying the page's own data fetching by ~0.5-1s. Providers follow their props,
  // so late-arriving data propagates to consumers.
  return (
    <CountryProvider countries={countries?.results.flatMap((x) => x) ?? []}>
      <LogoProvider options={data}>{props.children}</LogoProvider>
    </CountryProvider>
  );
}
